import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  archivePiratenpostThread,
  blockPiratenpostUser,
  loadPiratenpostInbox,
  loadPiratenpostThread,
  markPiratenpostThreadRead,
  reportPiratenpostMessage,
  searchPiratenpostUsers,
  sendFlaschenpost,
} from '../api/piratenpostApi';
import { currentUser } from '../utils/runtime';

function formatDate(value) {
  if (!value) return '';
  const date = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function threadTitle(thread) {
  return thread?.other_user?.name || 'Unbekannter Pirat';
}

export default function PiratenpostPage() {
  const location = useLocation();
  const [inbox, setInbox] = useState({ threads: [], unread_count: 0 });
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [threadPayload, setThreadPayload] = useState(null);
  const [loadingInbox, setLoadingInbox] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [error, setError] = useState('');
  const [composeSearch, setComposeSearch] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [composeBody, setComposeBody] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending] = useState(false);
  const [reportedMessageId, setReportedMessageId] = useState(null);
  const messagesEndRef = useRef(null);

  const threads = Array.isArray(inbox?.threads) ? inbox.threads : [];
  const unreadCount = Number(inbox.unread_count || 0);
  const selectedThread = useMemo(
    () => threads.find((thread) => Number(thread.id) === Number(selectedThreadId)) || threadPayload?.thread || null,
    [threads, selectedThreadId, threadPayload]
  );

  const refreshInbox = useCallback(async () => {
    setLoadingInbox(true);
    setError('');
    try {
      const data = await loadPiratenpostInbox();
      setInbox({
        threads: Array.isArray(data?.threads) ? data.threads : [],
        unread_count: Number(data?.unread_count || 0),
      });
      if (!selectedThreadId && Array.isArray(data?.threads) && data.threads.length) {
        setSelectedThreadId(data.threads[0].id);
      }
    } catch (loadError) {
      setError(loadError.message || 'Piratenpost konnte nicht geladen werden.');
    } finally {
      setLoadingInbox(false);
    }
  }, [selectedThreadId]);

  useEffect(() => {
    refreshInbox();
  }, [refreshInbox]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const threadFromUrl = params.get('thread');
    if (threadFromUrl) {
      setSelectedThreadId(Number(threadFromUrl));
    }
  }, [location.search]);

  useEffect(() => {
    if (!selectedThreadId) {
      setThreadPayload(null);
      return;
    }

    let cancelled = false;
    setLoadingThread(true);
    setError('');

    loadPiratenpostThread(selectedThreadId)
      .then(async (data) => {
        if (cancelled) return;
        setThreadPayload(data);
        try {
          await markPiratenpostThreadRead(selectedThreadId);
          refreshInbox();
        } catch {}
      })
      .catch((loadError) => {
        if (!cancelled) {
          setError(loadError.message || 'Diese Piratenpost konnte nicht geladen werden.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingThread(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedThreadId, refreshInbox]);

  useEffect(() => {
    const query = composeSearch.trim();
    if (query.length < 2 || selectedUser) {
      setUserResults([]);
      return;
    }

    const timer = window.setTimeout(() => {
      searchPiratenpostUsers(query)
        .then((users) => setUserResults(Array.isArray(users) ? users : []))
        .catch(() => setUserResults([]));
    }, 250);

    return () => window.clearTimeout(timer);
  }, [composeSearch, selectedUser]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [threadPayload?.messages?.length]);

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setComposeSearch(user.name);
    setUserResults([]);
  };

  const handleSendNew = async () => {
    if (!selectedUser || !composeBody.trim()) return;
    setSending(true);
    setError('');
    try {
      const data = await sendFlaschenpost({
        recipient_id: selectedUser.id,
        body: composeBody,
      });
      setThreadPayload(data);
      setSelectedThreadId(data?.thread?.id || null);
      setSelectedUser(null);
      setComposeSearch('');
      setComposeBody('');
      await refreshInbox();
    } catch (sendError) {
      setError(sendError.message || 'Die Flaschenpost konnte nicht verschickt werden.');
    } finally {
      setSending(false);
    }
  };

  const handleReply = async () => {
    if (!selectedThreadId || !replyBody.trim()) return;
    setSending(true);
    setError('');
    try {
      const data = await sendFlaschenpost({
        thread_id: selectedThreadId,
        body: replyBody,
      });
      setThreadPayload(data);
      setReplyBody('');
      await refreshInbox();
    } catch (sendError) {
      setError(sendError.message || 'Die Antwort konnte nicht verschickt werden.');
    } finally {
      setSending(false);
    }
  };

  const handleArchive = async () => {
    if (!selectedThreadId) return;
    try {
      const data = await archivePiratenpostThread(selectedThreadId);
      setInbox({
        threads: Array.isArray(data?.threads) ? data.threads : [],
        unread_count: Number(data?.unread_count || 0),
      });
      setSelectedThreadId(null);
      setThreadPayload(null);
    } catch (archiveError) {
      setError(archiveError.message || 'Piratenpost konnte nicht archiviert werden.');
    }
  };

  const handleBlock = async () => {
    const userId = selectedThread?.other_user?.id;
    if (!userId) return;
    try {
      await blockPiratenpostUser(userId);
      await handleArchive();
    } catch (blockError) {
      setError(blockError.message || 'Dieser Pirat konnte nicht blockiert werden.');
    }
  };

  const handleReport = async (messageId) => {
    if (!messageId || reportedMessageId === messageId) return;
    try {
      await reportPiratenpostMessage(messageId, 'In der App gemeldet');
      setReportedMessageId(messageId);
    } catch (reportError) {
      setError(reportError.message || 'Die Meldung konnte nicht gespeichert werden.');
    }
  };

  const handleBack = () => {
    if (selectedThreadId) {
      setSelectedThreadId(null);
      setThreadPayload(null);
      return;
    }
    window.history.back();
  };

  return (
    <main className="pfp-piratenpost-page">
      <div className="pfp-piratenpost-stage">
        <div className="pfp-piratenpost-header-row">
          <section className="pfp-piratenpost-hero">
            <div className="pfp-piratenpost-skull" aria-hidden="true">☠</div>
            <div>
              <span className="pfp-piratenpost-kicker">Private Nachrichten</span>
              <h1>Piratenpost</h1>
            </div>
            <span className="pfp-piratenpost-lantern pfp-piratenpost-lantern-left" aria-hidden="true" />
            <span className="pfp-piratenpost-lantern pfp-piratenpost-lantern-right" aria-hidden="true" />
          </section>

          <div className="pfp-piratenpost-toolbar" aria-label="Piratenpost Aktionen">
            <div className="pfp-piratenpost-toolbar-line">
              <button type="button" onClick={handleArchive} disabled={!selectedThreadId}>Archivieren</button>
              <button type="button" onClick={handleBlock} disabled={!selectedThreadId}>Blockieren</button>
            </div>
            <div className="pfp-piratenpost-toolbar-line is-secondary">
              <button type="button" onClick={refreshInbox}>Aktualisieren</button>
              <button type="button" onClick={handleBack}>Zurück</button>
            </div>
          </div>
        </div>

        {error && <div className="pfp-piratenpost-alert">{error}</div>}

        <section className="pfp-piratenpost-main-grid">
          <section className="pfp-piratenpost-compose">
            <div>
              <h2>Neue Flaschenpost</h2>
              <p>Suche einen Piraten und wirf deine Nachricht ins Wasser.</p>
            </div>
            <div className="pfp-piratenpost-compose-grid">
              <div className="pfp-piratenpost-search">
                <input
                  type="search"
                  value={composeSearch}
                  placeholder="Piraten suchen"
                  onChange={(event) => {
                    setComposeSearch(event.target.value);
                    setSelectedUser(null);
                  }}
                />
                {selectedUser && (
                  <button type="button" onClick={() => {
                    setSelectedUser(null);
                    setComposeSearch('');
                  }}>
                    Auswahl lösen
                  </button>
                )}
                {userResults.length > 0 && (
                  <div className="pfp-piratenpost-user-results">
                    {userResults.map((user) => (
                      <button type="button" key={user.id} onClick={() => handleSelectUser(user)}>
                        {user.avatar_url ? <img src={user.avatar_url} alt="" /> : <span />}
                        <strong>{user.name}</strong>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <textarea
                value={composeBody}
                maxLength={1200}
                placeholder="Was soll in die Flaschenpost?"
                onChange={(event) => setComposeBody(event.target.value)}
              />
              <button
                type="button"
                className="pfp-piratenpost-primary"
                disabled={!selectedUser || !composeBody.trim() || sending}
                onClick={handleSendNew}
              >
                Flaschenpost senden
              </button>
            </div>
          </section>

          <section className="pfp-piratenpost-conversation">
            <div className="pfp-piratenpost-conversation-title">
              <span>Gespräch mit</span>
              <h2>{selectedThreadId ? threadTitle(selectedThread) : 'Name'}</h2>
            </div>

            {!selectedThreadId && (
              <div className="pfp-piratenpost-empty pfp-piratenpost-empty-large">
                Wähle unten eine Flaschenpost oder schreibe eine neue Nachricht.
              </div>
            )}

            {selectedThreadId && (
              <>
                {loadingThread ? (
                  <div className="pfp-piratenpost-empty">Flaschenpost wird entkorkt.</div>
                ) : (
                  <div className="pfp-piratenpost-messages">
                    {(threadPayload?.messages || []).map((message) => (
                      <article
                        key={message.id}
                        className={`pfp-piratenpost-message ${message.is_mine ? 'is-mine' : ''}`}
                      >
                        <div>
                          <strong>{message.is_mine ? currentUser.name : message.sender?.name}</strong>
                          <time>{formatDate(message.created_at)}</time>
                        </div>
                        <p>{message.body}</p>
                        {!message.is_mine && (
                          <button type="button" onClick={() => handleReport(message.id)}>
                            {reportedMessageId === message.id ? 'Gemeldet' : 'Melden'}
                          </button>
                        )}
                      </article>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                )}

                <div className="pfp-piratenpost-reply">
                  <textarea
                    value={replyBody}
                    maxLength={1200}
                    placeholder="Antwort als Flaschenpost schreiben"
                    onChange={(event) => setReplyBody(event.target.value)}
                  />
                  <button
                    type="button"
                    className="pfp-piratenpost-primary"
                    disabled={!replyBody.trim() || sending}
                    onClick={handleReply}
                  >
                    Antworten
                  </button>
                </div>
              </>
            )}
          </section>
        </section>

        <aside className="pfp-piratenpost-inbox">
          <div className="pfp-piratenpost-inbox-count">
            {unreadCount} neue Flaschenpost{unreadCount === 1 ? '' : 'en'}
          </div>

          <div className="pfp-piratenpost-thread-list">
            {loadingInbox && <div className="pfp-piratenpost-empty">Piratenpost wird geladen.</div>}
            {!loadingInbox && threads.length === 0 && (
              <div className="pfp-piratenpost-empty">Noch keine Piratenpost im Postfach.</div>
            )}
            {threads.map((thread) => (
              <button
                type="button"
                key={thread.id}
                className={`pfp-piratenpost-thread ${Number(thread.id) === Number(selectedThreadId) ? 'is-active' : ''}`}
                onClick={() => setSelectedThreadId(thread.id)}
              >
                <span className="pfp-piratenpost-avatar">
                  {thread.other_user?.avatar_url ? <img src={thread.other_user.avatar_url} alt="" /> : 'P'}
                </span>
                <span>
                  <strong>{threadTitle(thread)}</strong>
                  <em>{thread.last_message?.body || 'Neue Unterhaltung'}</em>
                </span>
                {thread.unread_count > 0 && <b>{thread.unread_count > 9 ? '9+' : thread.unread_count}</b>}
              </button>
            ))}
          </div>
        </aside>
        <div className="pfp-piratenpost-props" aria-hidden="true" />
      </div>
    </main>
  );
}
